/**
 * Student-facing page to retrieve a recording link by entering their passcode.
 * Lives at /students/recording (linked from the Students passcode entry).
 */
import React, { useState } from "react";
import { api } from "../api";

export default function StudentRecording() {
  const [passcode, setPasscode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [link, setLink] = useState(null);

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setLink(null);
    setLoading(true);
    try {
      const data = await api.studentRecording(passcode.trim());
      setLink(data.download_link);
    } catch (err) {
      setError(err.message || "No recording available");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main
      className="min-h-[100dvh] bg-[#FAF9F6] pt-24 md:pt-32 pb-12 px-6"
      data-testid="student-recording-page"
    >
      <div className="max-w-md mx-auto">
        <div className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/60 mb-4">
          Lesson recording
        </div>
        <h1 className="font-display text-4xl md:text-5xl leading-tight tracking-tighter font-light text-[#1C1C1C]">
          Watch your lesson again.
        </h1>
        <p className="mt-4 text-base text-[#1C1C1C]/70 leading-relaxed">
          Enter the same passcode you used for the lesson. Recordings appear a
          few minutes after the session ends.
        </p>

        <form onSubmit={onSubmit} className="mt-10 space-y-4">
          <input
            data-testid="student-recording-input"
            type="text"
            autoFocus
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            placeholder="Lesson passcode"
            className="w-full px-4 py-3 bg-white border border-[#1C1C1C]/10 rounded-xl text-base font-mono tracking-wider focus:outline-none focus:border-[#1C1C1C]/40 transition-colors"
            disabled={loading}
          />
          {error && (
            <div className="text-sm text-[#B83A3A]" data-testid="student-recording-error">
              {error}
            </div>
          )}
          <button
            data-testid="student-recording-submit"
            type="submit"
            disabled={loading || !passcode.trim()}
            className="w-full px-5 py-3 bg-[#1C1C1C] text-[#FAF9F6] rounded-xl text-sm tracking-[0.16em] uppercase font-medium hover:bg-[#1C1C1C]/85 disabled:opacity-40 transition-colors"
          >
            {loading ? "Retrieving…" : "Get recording"}
          </button>
        </form>

        {link && (
          <div className="mt-10 p-5 bg-white rounded-xl border border-[#1C1C1C]/10" data-testid="student-recording-result">
            <div className="text-[11px] tracking-[0.28em] uppercase font-medium text-[#1C1C1C]/60 mb-2">
              Ready
            </div>
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-base text-[#1C1C1C] underline underline-offset-4 hover:opacity-70"
            >
              Open recording (link valid for 1 hour)
            </a>
          </div>
        )}
      </div>
    </main>
  );
}
